/**
 * Vacation Request Deletion Functionality
 */

import { Toast } from './toast.js';

class VacationRequestDeleter {
  constructor() {
    this.initializeEventListeners();
  }

  initializeEventListeners() {
    // Single delete buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-delete-vacation]')) {
        const button = e.target.closest('[data-delete-vacation]');
        const requestId = button.dataset.deleteVacation;
        this.deleteRequest(requestId, button);
      }
    });

    // Bulk delete functionality
    const bulkDeleteBtn = document.getElementById('bulkDeleteBtn');
    const selectAllCheckbox = document.getElementById('selectAllVacations');
    
    if (bulkDeleteBtn) {
      bulkDeleteBtn.addEventListener('click', () => this.bulkDelete());
    }
    
    if (selectAllCheckbox) {
      selectAllCheckbox.addEventListener('change', (e) => this.toggleSelectAll(e.target.checked));
    }

    // Update bulk delete button visibility
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-vacation-checkbox]')) {
        this.updateBulkDeleteButton();
      }
    });
  }

  deleteRequest(requestId, button = null) {
    Toast.confirm(
      'Are you sure you want to delete this vacation request? This action cannot be undone.',
      () => this.performDelete(requestId, button)
    );
  }

  async performDelete(requestId, button = null) {
    const originalText = button ? button.innerHTML : '';
    
    if (button) {
      button.disabled = true;
      button.innerHTML = '<span class="material-symbols-outlined text-sm animate-spin">sync</span> Deleting...';
    }

    try {
      const response = await fetch(window.wpAjax.ajaxUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'delete_vacation_request',
          request_id: requestId,
          nonce: window.wpAjax.deleteNonce
        })
      });

      const data = await response.json();

      if (data.success) {
        // Remove the row from the table
        const row = button ? button.closest('tr') : document.querySelector(`[data-vacation-row="${requestId}"]`);
        if (row) {
          row.remove();
        }
        Toast.show('Vacation request deleted successfully.', 'success');
        this.updateBulkDeleteButton();
      } else {
        throw new Error(data.data?.message || 'Failed to delete request');
      }
    } catch (error) {
      console.error('Error:', error);
      Toast.show('An error occurred while deleting the request.', 'error');
      
      if (button) {
        button.disabled = false;
        button.innerHTML = originalText;
      }
    }
  }

  bulkDelete() {
    const checkedBoxes = document.querySelectorAll('[data-vacation-checkbox]:checked');
    const requestIds = Array.from(checkedBoxes).map(cb => cb.value);
    
    if (requestIds.length === 0) {
      Toast.show('Please select at least one request to delete.', 'warning');
      return;
    }

    Toast.confirm(
      `Are you sure you want to delete ${requestIds.length} vacation request(s)? This action cannot be undone.`,
      () => this.performBulkDelete(requestIds)
    );
  }

  async performBulkDelete(requestIds) {
    const bulkBtn = document.getElementById('bulkDeleteBtn');
    const originalText = bulkBtn.innerHTML;
    
    bulkBtn.disabled = true;
    bulkBtn.innerHTML = '<span class="material-symbols-outlined text-sm animate-spin">sync</span> Deleting...';

    try {
      const response = await fetch(window.wpAjax.ajaxUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'bulk_delete_vacation_requests',
          request_ids: requestIds.join(','),
          nonce: window.wpAjax.deleteNonce
        })
      });

      const data = await response.json();

      if (data.success) {
        // Remove all deleted rows
        requestIds.forEach(id => {
          const row = document.querySelector(`[data-vacation-row="${id}"]`);
          if (row) row.remove();
        });
        
        Toast.show(`${requestIds.length} vacation request(s) deleted successfully.`, 'success');
        this.updateBulkDeleteButton();
        document.getElementById('selectAllVacations').checked = false;
      } else {
        throw new Error(data.data?.message || 'Failed to delete requests');
      }
    } catch (error) {
      console.error('Error:', error);
      Toast.show('An error occurred while deleting the requests.', 'error');
    } finally {
      bulkBtn.disabled = false;
      bulkBtn.innerHTML = originalText;
    }
  }

  toggleSelectAll(checked) {
    const checkboxes = document.querySelectorAll('[data-vacation-checkbox]');
    checkboxes.forEach(cb => cb.checked = checked);
    this.updateBulkDeleteButton();
  }

  updateBulkDeleteButton() {
    const checkedBoxes = document.querySelectorAll('[data-vacation-checkbox]:checked');
    const bulkBtn = document.getElementById('bulkDeleteBtn');
    const selectAllCheckbox = document.getElementById('selectAllVacations');
    
    if (bulkBtn) {
      bulkBtn.style.display = checkedBoxes.length > 0 ? 'block' : 'none';
    }
    
    // Update select all checkbox state
    if (selectAllCheckbox) {
      const allCheckboxes = document.querySelectorAll('[data-vacation-checkbox]');
      selectAllCheckbox.checked = allCheckboxes.length > 0 && checkedBoxes.length === allCheckboxes.length;
    }
  }
}

// Initialize when DOM is ready
export function initVacationRequestDeleter() {
  return new VacationRequestDeleter();
}
